import { useContext, useMemo } from 'react'
import { MarkupContext } from '../../providers/Markup'
import Logo from './Logo'
import MobileNav from './MobileNav'
import Search from './Search'
import CtaDropDown from './CTA'
import MainNav from './MainNav'
import MegaMenuNav from './MegaMenuNav'
import { LockClosedIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import ButtonEl from '../helpers/ctas/Button'
import { dataLayerPush } from '../../helpers/thirdparty'
import { useMemberSession, getCsrfToken, getReturnUrl } from '../../helpers/memberSession'

const MY_PROFILE_URL = '/member'
const LOGOUT_URL = '/member/logout'

const LoginMenu = () => {
  return (
    <button
      className="flex h-full items-center justify-center gap-2 px-8 bg-(--color-secondary) text-white transition-opacity hover:opacity-90 focus:outline-none"
      aria-haspopup="true"
      aria-expanded="false"
    >
      {/* Lock Icon */}
      <LockClosedIcon className="w-5 h-5 stroke-2" aria-hidden="true" />

      {/* Text */}
      <span className="text-sm font-medium leading-none">Login</span>

      {/* Chevron Icon */}
      <ChevronDownIcon className="w-4 h-4 stroke-2" aria-hidden="true" />
    </button>
  )
}

function Header(props) {
  const { detectPaletteClass } = useContext(MarkupContext)

  // Member auth state — drives the Login/Logout + My Profile swap in the header.
  const { isAuthenticated, needsRenewal } = useMemberSession()

  // Check if headerDataSource is available and has megaMenuColumns
  const hasHeaderDataSource = useMemo(() => {
    return props.headerDataSource?.megaMenuColumns?.length > 0
  }, [props.headerDataSource])

  const megaMenuColumns = useMemo(() => {
    return props.headerDataSource?.megaMenuColumns
  }, [props.headerDataSource])

  // Top nav bar related conditions
  const hasTopNav = props.headerDataSource?.topNavigationBar?.length > 0
  const topNavBar = props.headerDataSource?.topNavigationBar

  const joinLinkData = props.headerDataSource?.joinLink
  const loginLinkData = props.headerDataSource?.loginLink
  // My Profile URL is configurable on the Website node (Membership tab → My Profile Link),
  // same place as the Login / Join Us links. Falls back to /member when not set.
  const myProfileUrl = props.headerDataSource?.myProfileLink?.link?.url || MY_PROFILE_URL

  return (
    <header className={detectPaletteClass(`bg-base`)}>
      {props.children}

      {/* --- SHARED WRAPPER --- */}
      {/* This outer container centers the layout on the page */}
      <div className="container mx-auto px-6 sm:px-0">
        
        {/* This inner wrapper forces the Top Nav to stretch and match the width of the Main Nav if it overflows */}
        <div className="flex flex-col min-w-full w-max">
          
          {/* --- NEW TOP NAV BAR --- */}
          {hasTopNav && (
            <div className="hidden lg:!flex justify-end items-center w-full pt-3 pb-1 gap-6 text-xs font-normal text-(--sb-top-nav-bar-text)">
              {topNavBar.map((navItem, idx) => (
                <a
                  key={`topnav-item-${idx}`}
                  href={navItem?.link?.url}
                  target={navItem.link?.target}
                  className="hover:text-(--sb-top-nav-bar-text) transition-colors duration-200"
                  onClick={() => {
                    dataLayerPush({
                      event: 'navigation_menu_click',
                      category: navItem.link?.name,
                      click_text: navItem.link?.name,
                    })
                  }}
                >
                  {navItem.link?.name}
                </a>
              ))}
            </div>
          )}
          {/* ----------------------- */}

          {/* --- MAIN NAV BAR --- */}
          <div className="flex justify-between items-stretch w-full h-[80px] flex-nowrap">
            <div className="flex self-center leading-none shrink-0 xl:mr-4 2xl:mr-16">
              <Logo deskLogo={props.deskLogo} mobLogo={props.mobLogo} />
            </div>
            
            <div className="flex self-center h-full flex-1">
              {!props.res.hideMegamenu &&
                (hasHeaderDataSource ? (
                  <MegaMenuNav
                    {...props}
                    megaMenuColumns={megaMenuColumns}
                    type="desktop"
                    setMenuActive={props.setMenuActive}
                    menuActive={props.menuActive}
                  />
                ) : (
                  <MainNav {...props} nav={props.nav} type="desktop" />
                ))}
            </div>
            
            <div className="flex items-center gap-2 xl:gap-3 2xl:gap-4 shrink-0">
              <div className={`flex items-center ${props.menuActive ? ' hidden xl:flex' : ''}`}>
                <Search {...props} />
              </div>

              {isAuthenticated ? (
                <>
                  <div className="hidden xl:!flex relative">
                    <ButtonEl
                      theme="primary"
                      link={{ url: myProfileUrl, name: 'My Profile' }}
                      className={`!px-[14px] !py-[10px]`}
                      onClick={() => {
                        dataLayerPush({
                          event: 'navigation_menu_click',
                          category: 'My Profile',
                          click_text: 'My Profile',
                        })
                      }}
                    />
                    {needsRenewal && (
                      <a
                        href="/member/renewals"
                        className="absolute -top-2 -right-2 inline-flex items-center justify-center w-6 h-6 bg-white hover:bg-[color-mix(in_oklch,var(--color-nds-orange)_10%,white)] border-2 border-(--color-nds-orange) rounded-full transition-colors !text-(--color-nds-orange)"
                        role="status"
                        aria-label="Membership renewal required — click to renew"
                      >
                        <span className="text-sm font-bold leading-none !text-(--color-nds-orange)">!</span>
                      </a>
                    )}
                  </div>
                  <div className="hidden xl:!flex">
                    <form action={LOGOUT_URL} method="post" className="inline-flex">
                      <input
                        type="hidden"
                        name="__RequestVerificationToken"
                        value={getCsrfToken()}
                      />
                      <input type="hidden" name="returnUrl" value={getReturnUrl()} />
                      <ButtonEl
                        type="submit"
                        theme="secondary"
                        className={`!px-[14px] !py-[10px] !text-(--color-border-button-secondary-text) hover:!text-(--color-button-secondary-hover-text)`}
                        onClick={() => {
                          dataLayerPush({
                            event: 'logout_intent',
                            click_text: 'Logout',
                            page_section: 'Header',
                          })
                        }}
                      >
                        Logout
                      </ButtonEl>
                    </form>
                  </div>
                </>
              ) : (
                <>
                  {joinLinkData && (
                    <div className="hidden xl:!flex">
                      <ButtonEl
                        icon={joinLinkData.linkIconClass}
                        position={joinLinkData.linkPosition}
                        theme="secondary"
                        target={joinLinkData.link?.target}
                        link={joinLinkData.link}
                        className={`!px-[14px] !py-[10px] !text-(--color-border-button-secondary-text) hover:!text-(--color-button-secondary-hover-text)`}
                        skipCtaClickTracking
                        onClick={() => {
                          dataLayerPush({
                            event: 'signup_intent',
                          })
                        }}
                      />
                    </div>
                  )}
                  {loginLinkData && (
                    <div className="hidden xl:!flex">
                      <ButtonEl
                        icon={loginLinkData.linkIconClass}
                        position={loginLinkData.linkPosition || 'After'}
                        theme="primary"
                        target={loginLinkData.link?.target}
                        link={loginLinkData.link}
                        className={`!px-[14px] !py-[10px]`}
                        skipCtaClickTracking
                        onClick={() => {
                          dataLayerPush({
                            event: 'login_intent',
                            click_text: 'Join Us',
                            page_section: 'Header',
                            category: null,
                            member_only: false,
                          })
                        }}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            {!props.res.hideMegamenu && (
              <MobileNav {...props} nav={props.nav} type="mobile" megaMenuColumns={megaMenuColumns} />
            )}
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header